import { Card, EmptyState, Spinner } from "../../shared/ui/index.js";
import { useToast } from "../../shared/toast.js";
import { useCouplePhotoMutations } from "./weddings.api.js";
import type { CouplePhoto } from "../../shared/types.js";

export function CouplePhotosPanel({
  weddingId,
  photos,
}: {
  weddingId: string;
  photos: CouplePhoto[];
}) {
  const { add, remove, reorder } = useCouplePhotoMutations(weddingId);
  const toast = useToast();

  const move = (index: number, dir: -1 | 1) => {
    const ids = photos.map((p) => p.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j]!, ids[index]!];
    reorder.mutate(ids);
  };

  return (
    <Card id="photos">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg text-stone-900">Photos du couple</h2>
        <span className="text-xs text-stone-400">{photos.length}</span>
      </div>
      <p className="mt-1 text-sm text-stone-500">Diaporama de la page d'accueil invité.</p>

      {photos.length > 0 ? (
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((p, i) => (
            <div key={p.id} className="group relative overflow-hidden rounded-lg">
              <img src={p.url} alt="" className="aspect-square w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/60 to-transparent p-1 opacity-0 transition group-hover:opacity-100">
                <div className="flex gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="rounded bg-white/90 px-1 text-xs disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === photos.length - 1}
                    className="rounded bg-white/90 px-1 text-xs disabled:opacity-30"
                  >
                    ↓
                  </button>
                </div>
                <button
                  onClick={() => remove.mutate(p.id)}
                  className="rounded bg-white/90 px-1 text-xs text-rose-600"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState>Aucune photo — ajoutez-en pour le diaporama d'accueil.</EmptyState>
        </div>
      )}

      <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-sm hover:border-stone-400">
        {add.isPending ? "Envoi…" : "Ajouter des photos"}
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          disabled={add.isPending}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length)
              add.mutate(files, {
                onSuccess: () => toast("Photo(s) ajoutée(s)"),
                onError: () => toast("Échec de l'envoi", "err"),
              });
            e.target.value = "";
          }}
        />
      </label>
      {add.isPending && (
        <div className="mt-2">
          <Spinner label="Envoi en cours…" />
        </div>
      )}
    </Card>
  );
}
